// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PDFViewer from "@eisinc/App/Components/PDFViewer/PDFViewer";
import { ProductDetailsVariantOptionsContext } from "@eisinc/App/Contexts/ProductDetailsVariantOptionsContext";
import { breadcrumbStyles } from "@eisinc/App/SharedStyles/GeneralStyles";
import { getComputedVariantProduct } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import Breadcrumbs from "@eisinc/MobiusOverrides/Breadcrumbs";
import { parseBoolean } from "@eisinc/XDL/Utilities";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getJsonLdScriptId from "@insite/client-framework/Common/Utilities/getJsonLdScriptId";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata, { Metadata } from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { HasDeferredApiContext, withCallDeferredApi } from "@insite/client-framework/Components/DeferredApiContext";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import prepareProduct from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/PrepareProduct";
import {
    getSelectedProductPath,
    getSession,
    getSettingsCollection,
    getShouldPreventCatalogLoop,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductState,
    getProductStateByPath,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeUnitOfMeasure";
import displayProduct from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import generateStructuredPageData from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import loadProductPricingAndInventory from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/LoadProductPricingAndInventory";
import resetSelections from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ResetSelections";
import setMetadataSetForId from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetMetadataSetForId";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HideFooter, HideHeader } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CurrentCategory from "@insite/content-library/Components/CurrentCategory";
import Modals from "@insite/content-library/Components/Modals";
import { generateLinksFrom } from "@insite/content-library/Components/PageBreadcrumbs";
import { Typography } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ productPath: string }>(location.search).productPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);

    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }

    const productState = getProductStateByPath(state, productPath);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
    const variantChildren = getVariantChildrenDataView(state, productState.id);
    const settingsCollection = getSettingsCollection(state);
    const homePageUrl = getHomePageUrl(state);

    // XNG-Changes
    const smartCodeAbTestingSettings = (settingsCollection as any).smartCodeAbTestingSettings || {};
    return {
        productState: computedVariantProduct,
        variantProductState,
        catalogPageState,
        parentProductState: productState,
        productInfo:
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined,
        productPath,
        queryString,
        lastProductPath: state.pages.productDetails.lastProductPath,
        lastStyledOption: state.pages.productDetails.lastStyledOption,
        websiteName: state.context.website.name,
        page: getCurrentPage(state),
        location,
        websiteSettings: settingsCollection.websiteSettings,
        searchPath: settingsCollection.searchSettings.searchPath,
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl,
        selectedProductId: state.pages.productDetails.selectedProductId,
        isProductLoading: state.pages.productDetails.isProductLoading,
        metadataSetForId: state.pages.productDetails.metadataSetForId,
        structuredPageData: state.pages.productDetails.structuredPageData,
        structuredPageDataSetForId: state.pages.productDetails.structuredPageDataSetForId,
        session: getSession(state),
        productInfosById: state.pages.productDetails.productInfosById,
        variantChildren,
        enableServerSideRendering: settingsCollection.websiteSettings.enableServerSideRendering,
        // XNG-Changes
        enableVwoPdpWindowSetting: parseBoolean(smartCodeAbTestingSettings?.enableVwoPdpWindowSetting),
        vwoPdpWindowUrlValue: smartCodeAbTestingSettings?.vwoPdpWindowUrlValue,
    };
};

const mapDispatchToProps = {
    displayProduct,
    changeQtyOrdered,
    changeUnitOfMeasure,
    resetSelections,
    generateStructuredPageData,
    setMetadataSetForId,
    loadProductPricingAndInventory,
    prepareProduct,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    PageProps &
    HasDeferredApiContext;
interface State {
    metadataSetForId?: string;
    variantValues?: [];
    variantIds?: [];
}

// XNG-Change: add isLoading to productModel and ProductContext
export interface XProductModel extends ProductModel {
    isLoading?: boolean;
}
export interface XProductContextModel extends ProductContextModel {
    isLoading?: boolean;
}

class ProductDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            variantValues: [],
            variantIds: [],
        };
    }

    UNSAFE_componentWillMount() {
        // XNG-Changes: Set the VWO identifier when PDP loads
        if (typeof window !== "undefined" && this.props.enableVwoPdpWindowSetting && this.props.vwoPdpWindowUrlValue) {
            (window as any)._vis_opt_url = this.props.vwoPdpWindowUrlValue;
        }

        if (!getShouldPreventCatalogLoop(this.props.deferredApiCalls)) {
            this.displayProductIfNeeded();
        }
        this.props.callDeferredApis({
            pricingHandler: () => {
                this.props.loadProductPricingAndInventory({
                    product: this.props.productState.value,
                    productInfosById: this.props.productInfosById,
                    variantChildren: this.props.variantChildren.value,
                    styledOption: this.getStyledOption(),
                });
            },
            inventoryHandler: () => {
                this.props.loadProductPricingAndInventory({
                    product: this.props.productState.value,
                    productInfosById: this.props.productInfosById,
                    variantChildren: this.props.variantChildren.value,
                    styledOption: this.getStyledOption(),
                });
            },
        });
        this.trySetMetadata(true);
    }

    componentDidMount(): void {
        if (this.props.enableServerSideRendering) {
            const {
                productState: { value: product },
            } = this.props;

            if (!product) {
                return;
            }

            this.props.prepareProduct({ product });
        }
    }

    componentDidUpdate(): void {
        this.displayProductIfNeeded();

        const { productState } = this.props;

        if (productState.value && productState.value.id !== this.props.metadataSetForId) {
            trackPageChange();

            this.trySetMetadata(true);
        }

        this.trySetMetadata(false);
    }

    componentWillUnmount() {
        // XNG-Changes: Delete the VWO identifier when PDP unmounts to prevent it from affecting other pages
        if (typeof window !== "undefined" && (window as any)._vis_opt_url) {
            delete (window as any)._vis_opt_url;
        }
        this.props.resetSelections();
    }

    trySetMetadata(setTheState: boolean) {
        const {
            productState: { value: product },
            catalogPageState,
            websiteName,
            location,
            websiteSettings,
            page,
            structuredPageData,
            productInfo,
            session,
            metadataSetForId,
            structuredPageDataSetForId,
        } = this.props;

        if (!catalogPageState?.value || !product) {
            return false;
        }

        if (websiteSettings.enableProductStructuredData && product.id !== structuredPageDataSetForId) {
            this.props.generateStructuredPageData({
                product,
                productInfo,
                session,
                brandId: catalogPageState.value.brandId || product.brand?.id,
                categoryIdWithBrandId: catalogPageState.value.categoryIdWithBrandId,
                categoryId: catalogPageState.value.categoryId,
            });
            return false;
        }

        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            alternateLanguageUrls,
        } = catalogPageState.value;

        const pageMetaData: Metadata = {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            currentPath: location.pathname,
            canonicalPath: product.canonicalUrl,
            title: title || product.productTitle,
            websiteName,
            alternateLanguageUrls: alternateLanguageUrls ?? undefined,
            structuredPageData,
            jsonLdScriptId: getJsonLdScriptId(page.type),
        };

        if (catalogPageState.value.productId !== this.props.selectedProductId) {
            const variantContent = this.props.variantProductState.value?.content;
            if (variantContent) {
                if (typeof window === "undefined" || window === null) {
                    return;
                }
                const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
                const urlAddress = `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}`;
                const imagePath =
                    variantContent.openGraphImage ||
                    product.smallImagePath ||
                    product.mediumImagePath ||
                    product.largeImagePath;

                const openGraphImage =
                    !imagePath || imagePath.indexOf("http://") > -1 || imagePath.indexOf("https://") > -1
                        ? imagePath
                        : urlAddress + imagePath.startsWith("/")
                          ? imagePath
                          : `/${imagePath}`;
                let openGraphUrl = variantContent.openGraphUrl || product.canonicalUrl;

                openGraphUrl =
                    !openGraphUrl || openGraphUrl.indexOf("http://") > -1 || openGraphUrl.indexOf("https://") > -1
                        ? openGraphUrl
                        : urlAddress + openGraphUrl.startsWith("/")
                          ? openGraphUrl
                          : `/${openGraphUrl}`;
                const pageTitle = variantContent.pageTitle || product.productTitle;

                pageMetaData.title = pageTitle;
                pageMetaData.metaKeywords =
                    variantContent.metaKeywords || `${product.productTitle} ${product.productNumber}`;
                pageMetaData.metaDescription = variantContent.metaDescription || product.productTitle;
                pageMetaData.openGraphTitle = variantContent.openGraphTitle || pageTitle;
                pageMetaData.openGraphImage = openGraphImage || "";
                pageMetaData.openGraphUrl = openGraphUrl || "";
            }
        }

        if (!openGraphImage && !pageMetaData.openGraphImage && product.images && product.images?.length > 0) {
            pageMetaData.openGraphImage = product.images[0].smallImagePath || product.images[0].mediumImagePath;
        }

        setPageMetadata(pageMetaData, websiteSettings);

        // <XNG-Change> if the same product, even if setTheState = true, no need to call setMetadataSetForId
        if (setTheState && product.id !== metadataSetForId) {
            this.props.setMetadataSetForId({
                metadataSetForId: product.id,
            });
        }

        return true;
    }

    getStyledOption() {
        const { queryString } = this.props;
        const queryParams = parseQueryString<{ option?: string; criteria?: string }>(queryString.replace("?", ""));
        return (queryParams.option?.toString() || queryParams.criteria?.toString() || "").toLocaleLowerCase();
    }

    displayProductIfNeeded() {
        const { productPath, queryString, lastProductPath, lastStyledOption, isProductLoading, searchPath } =
            this.props;
        const styledOption = this.getStyledOption();

        if (
            (productPath.toLowerCase() === lastProductPath?.toLowerCase() &&
                styledOption === lastStyledOption?.toLowerCase()) ||
            productPath.toLowerCase() === `/${searchPath.toLowerCase()}`
        ) {
            return;
        }

        if (isProductLoading) {
            return;
        }

        this.props.displayProduct({ path: productPath, styledOption });
    }

    setVariantValues = (variantValues: []) => {
        this.setState({
            variantValues,
        });
    };

    setVariantIds = (variantIds: []) => {
        this.setState({
            variantIds,
        });
    };

    render() {
        const viewDocumentParameters: any = parseQueryString<{ fileName: string; filePath: string }>(
            this.props.location.search,
        );
        // XNG-Change
        let isLoading = false;

        if (
            !this.props.productState.value ||
            this.props.productState.isLoading ||
            !this.props.productInfo ||
            // XNG-Changes: check !this.props.selectedProductId
            !this.props.selectedProductId ||
            (this.props.productPath &&
                this.props.lastProductPath &&
                this.props.productPath.toLowerCase() !== this.props.lastProductPath.toLowerCase())
        ) {
            isLoading = true;
        }

        const { variantValues, variantIds } = this.state;
        // xng-change
        const productId = this.props.productState.value?.id || "";
        const productContext: XProductContextModel = {
            // <xng-change - hide customer part number for parent products
            product: {
                ...this.props.productState.value,
                customerProductNumber: this.props.productState?.value?.isVariantParent
                    ? ""
                    : this.props.productState?.value?.customerProductNumber || "",
                isLoading,
            } as XProductModel,
            isLoading,
            // </xng-change>
            productInfo: this.props.productInfo as ProductInfo,
            onQtyOrderedChanged: o => {
                this.props.changeQtyOrdered({
                    qtyOrdered: o,
                    productId,
                });
            },
            onUnitOfMeasureChanged: o => {
                this.props.changeUnitOfMeasure({
                    unitOfMeasure: o,
                    productId,
                });
            },
        };

        if (viewDocumentParameters.fileName && viewDocumentParameters.filePath) {
            const homePageLink = { children: translate("Home"), href: "/" };
            const updatedLinks = [] as any;

            let links =
                this.props.links || generateLinksFrom(this.props.linksState, this.props.nodeId, this.props.homePageUrl);

            if (links.length > 0 && links[0].href !== homePageLink.href) {
                links = [homePageLink, ...links];
            }

            const lastLink = links.length > 0 && links[links.length - 1];

            if (lastLink) {
                const linksLength = links.length;

                for (let i = 0; i < linksLength - 1; i++) {
                    updatedLinks.push(links[i]);
                }

                updatedLinks.push({
                    children: lastLink.children,
                    href: this.props.location.pathname,
                });

                updatedLinks.push({
                    children: `${translate("Document/")}${viewDocumentParameters.fileName}`,
                    href: "",
                });
            }

            return (
                <Page data-test-selector={`productDetails_productId_${productId}`}>
                    <CurrentCategory>
                        <ProductContext.Provider value={productContext}>
                            <ParentProductIdContext.Provider value={this.props.parentProductState?.value?.id}>
                                <Breadcrumbs
                                    {...breadcrumbStyles}
                                    hideCurrentPage={false}
                                    links={updatedLinks}
                                    data-test-selector="pageBreadcrumbs"
                                />
                                <StyledWrapper
                                    css={css`
                                        margin-top: 15px;
                                    `}
                                >
                                    <Typography variant="h2" as="h1" weight="normal">
                                        {viewDocumentParameters.fileName}
                                    </Typography>
                                </StyledWrapper>
                                <PDFViewer
                                    filePath={viewDocumentParameters.filePath}
                                    fileName={viewDocumentParameters.fileName}
                                    dataTestSelector={`productDetails_${viewDocumentParameters.fileName}_${viewDocumentParameters.filePath}`}
                                    asLink={viewDocumentParameters.doNotEmbed === "true"}
                                    newTab={viewDocumentParameters.doNotEmbed === "true"}
                                />
                            </ParentProductIdContext.Provider>
                        </ProductContext.Provider>
                    </CurrentCategory>
                    <Modals />
                </Page>
            );
        }

        return (
            <Page data-test-selector={`productDetails_productId_${productId}`}>
                {/* XNG-Changes: Removed `selectedProductId` check, already validated above */}
                {/* {this.props.selectedProductId && ( */}
                <CurrentCategory>
                    <ProductContext.Provider value={productContext}>
                        <ParentProductIdContext.Provider value={this.props.parentProductState?.value?.id}>
                            <ProductDetailsVariantOptionsContext.Provider
                                value={{
                                    variantValues,
                                    variantIds,
                                    setVariantValues: this.setVariantValues,
                                    setVariantIds: this.setVariantIds,
                                }}
                            >
                                <Zone contentId={this.props.id} zoneName="Content" />
                            </ProductDetailsVariantOptionsContext.Provider>
                        </ParentProductIdContext.Provider>
                    </ProductContext.Provider>
                </CurrentCategory>
                {/* )} */}
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCallDeferredApi(ProductDetailsPage)),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: false,
        supportsProductSelection: true,
        pageType: "System",
        fieldDefinitions: [HideHeader, HideFooter],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "ProductDetailsPage" instead of this constant.
 */
export const ProductDetailsPageContext = "ProductDetailsPage";
